var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useState } from "react";
import { faSort, faSortDown, faSortUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./SortableDataTable.css";
var SortableDataTable = function (_a) {
    var columns = _a.columns, data = _a.data, _b = _a.className, className = _b === void 0 ? "" : _b, _c = _a.containerClassName, containerClassName = _c === void 0 ? "" : _c, _d = _a.loading, loading = _d === void 0 ? false : _d, _e = _a.emptyMessage, emptyMessage = _e === void 0 ? "No data available" : _e, onSort = _a.onSort, defaultSortKey = _a.defaultSortKey, _f = _a.defaultSortDirection, defaultSortDirection = _f === void 0 ? "asc" : _f, _g = _a.expandableRows, expandableRows = _g === void 0 ? false : _g, renderExpandedRow = _a.renderExpandedRow, _h = _a.expandedRows, expandedRows = _h === void 0 ? new Set() : _h, onToggleExpanded = _a.onToggleExpanded, _j = _a.getRowId, getRowId = _j === void 0 ? function (_, index) { return index; } : _j, sectionHeader = _a.sectionHeader, _k = _a.dimmed, dimmed = _k === void 0 ? false : _k;
    var _l = useState(defaultSortKey || null), sortKey = _l[0], setSortKey = _l[1];
    var _m = useState(defaultSortDirection), sortDirection = _m[0], setSortDirection = _m[1];
    // Handle column header click for sorting
    var handleSort = function (columnKey, sortable) {
        if (sortable === void 0) { sortable = true; }
        if (!sortable)
            return;
        var newDirection = "asc";
        if (sortKey === columnKey) {
            if (sortDirection === "asc") {
                newDirection = "desc";
            }
            else if (sortDirection === "desc") {
                newDirection = null;
            }
            else {
                newDirection = "asc";
            }
        }
        setSortKey(newDirection ? columnKey : null);
        setSortDirection(newDirection);
        if (onSort) {
            onSort(columnKey, newDirection);
        }
    };
    // Sort data if no external sort handler is provided
    var sortedData = useMemo(function () {
        if (onSort || !sortKey || !sortDirection) {
            return data;
        }
        return __spreadArray([], data, true).sort(function (a, b) {
            var aValue = a[sortKey];
            var bValue = b[sortKey];
            // Handle null/undefined values
            if (aValue == null && bValue == null)
                return 0;
            if (aValue == null)
                return sortDirection === "asc" ? 1 : -1;
            if (bValue == null)
                return sortDirection === "asc" ? -1 : 1;
            // Handle different data types
            if (typeof aValue === "string" && typeof bValue === "string") {
                var result_1 = aValue.localeCompare(bValue);
                return sortDirection === "asc" ? result_1 : -result_1;
            }
            if (typeof aValue === "number" && typeof bValue === "number") {
                var result_2 = aValue - bValue;
                return sortDirection === "asc" ? result_2 : -result_2;
            }
            // Convert to strings for comparison
            var aStr = String(aValue);
            var bStr = String(bValue);
            var result = aStr.localeCompare(bStr);
            return sortDirection === "asc" ? result : -result;
        });
    }, [data, sortKey, sortDirection, onSort]);
    // Get sort icon for a column - only show when actively sorted or hovered
    var getSortIcon = function (columnKey, sortable) {
        if (sortable === void 0) { sortable = true; }
        if (!sortable)
            return null;
        // Only show icons for the currently sorted column
        if (sortKey !== columnKey) {
            return React.createElement(FontAwesomeIcon, { icon: faSort, className: "ps-sortable-table-sort-icon" });
        }
        if (sortDirection === "asc") {
            return React.createElement(FontAwesomeIcon, { icon: faSortUp, className: "ps-sortable-table-sort-icon" });
        }
        if (sortDirection === "desc") {
            return React.createElement(FontAwesomeIcon, { icon: faSortDown, className: "ps-sortable-table-sort-icon" });
        }
        return React.createElement(FontAwesomeIcon, { icon: faSort, className: "ps-sortable-table-sort-icon" });
    };
    // Handle row expansion toggle
    var _handleToggleExpanded = function (rowId) {
        if (onToggleExpanded) {
            onToggleExpanded(rowId);
        }
    };
    var tableContainerClasses = [
        "ps-sortable-table-container",
        loading && "ps-sortable-table-loading",
        containerClassName,
    ]
        .filter(Boolean)
        .join(" ");
    var tableClasses = ["ps-sortable-table", className].filter(Boolean).join(" ");
    return (React.createElement("div", null,
        sectionHeader && React.createElement("div", { className: "ps-sortable-table-section-header" }, sectionHeader),
        React.createElement("div", { className: tableContainerClasses }, sortedData.length === 0 && !loading ? (React.createElement("div", { className: "ps-sortable-table-empty" }, emptyMessage)) : (React.createElement("table", { className: tableClasses },
            React.createElement("thead", null,
                React.createElement("tr", null, columns.map(function (column) {
                    var headerClasses = [
                        "ps-sortable-table-header",
                        column.sortable !== false ? "sortable" : "non-sortable",
                        column.align ? "text-".concat(column.align) : "text-left",
                        sortKey === column.key ? "sorted" : "",
                        column.className || "",
                    ]
                        .filter(Boolean)
                        .join(" ");
                    return (React.createElement("th", { key: column.key, className: headerClasses, style: { width: column.width }, onClick: function () { return handleSort(column.key, column.sortable !== false); } },
                        column.label,
                        getSortIcon(column.key, column.sortable !== false)));
                }))),
            React.createElement("tbody", null, sortedData.map(function (row, index) {
                var rowId = getRowId(row, index);
                var isExpanded = expandedRows.has(rowId);
                return (React.createElement(React.Fragment, { key: rowId },
                    React.createElement("tr", { className: dimmed ? "dimmed" : "" }, columns.map(function (column) {
                        var cellClasses = [column.align ? "text-".concat(column.align) : "text-left", column.className]
                            .filter(Boolean)
                            .join(" ");
                        var cellContent;
                        if (column.render) {
                            cellContent = column.render(row[column.key], row, index);
                        }
                        else {
                            cellContent = row[column.key];
                        }
                        return (React.createElement("td", { key: column.key, className: cellClasses }, cellContent));
                    })),
                    expandableRows && isExpanded && renderExpandedRow && (React.createElement("tr", null,
                        React.createElement("td", { colSpan: columns.length, className: "ps-sortable-table-expandable-cell" },
                            React.createElement("div", { className: "ps-sortable-table-expandable-content" }, renderExpandedRow(row, index)))))));
            })))))));
};
export default SortableDataTable;
